<template>
  <form ref="form" data-form="account-settings" @submit.prevent="submit">
    <input hidden type="text" :value="email" autocomplete="email" />
    <UtilityFormField
      title="Set New Password"
      :field-id="inputId"
      :error="showValidation && newPassword.length > 0 ? error : undefined"
    >
      <input
        :id="inputId"
        :key="key"
        ref="input"
        v-model="newPassword"
        :disabled="disabled"
        type="password"
        name="password"
        data-input="text"
        autocomplete="new-password"
        :minlength="minPassword"
        @blur="showValidation = true"
      />
    </UtilityFormField>
    <div>
      <button
        type="submit"
        :disabled="submitting || !valid"
        data-btn="set-password status-icon"
      >
        <span class="set-password-text">Set Password</span>
        <UtilitySaveIndicator :state="saving" />
      </button>
    </div>
  </form>
</template>

<script lang="ts">
import { v4 as uuid } from 'uuid';
import { computed, defineComponent, ref } from 'vue';

import { useUserStore } from '@/js/stores/user';
import { SAVE_STATE, type SaveState } from '@/js/utils/constants';

export default defineComponent({
  name: 'AccountFormPasswordChange',
  props: {
    disabled: { type: Boolean, default: false },
  },
  setup() {
    const userStore = useUserStore();
    const email = userStore.user?.email || '';
    const newPassword = ref('');
    const error = ref('');
    const valid = ref(false);
    const showValidation = ref(false);
    const saving = ref<SaveState>(SAVE_STATE.INACTIVE);
    const form = ref(null);
    const save = (password: string) => userStore.update({ password });
    const submitting = computed(() => saving.value === SAVE_STATE.SAVING);
    const minPassword = window.CONFIG?.PASSWORD_MIN_LENGTH || 0;

    const inputId = `password-${uuid()}`;
    const key = ref(uuid());

    return {
      email,
      error,
      form,
      inputId,
      key,
      save,
      saving,
      showValidation,
      newPassword,
      submitting,
      valid,
      minPassword,
    };
  },
  watch: {
    newPassword() {
      const form = this.$refs.form as HTMLFormElement;
      if (form) {
        this.valid = form.checkValidity() && this.newPassword.length > 0;
      }
      const input = this.$refs.input as HTMLInputElement;
      this.error = input.validationMessage;
    },
  },
  methods: {
    async submit() {
      this.saving = SAVE_STATE.SAVING;
      await this.save(this.newPassword);
      this.saving = SAVE_STATE.SAVED;
      // Reset input and validation
      this.newPassword = '';
      this.key = uuid();
      this.showValidation = false;
    },
  },
});
</script>

<style lang="scss" scoped>
.set-password-text {
  grid-area: text;
}
</style>
